import React, { useMemo, useState } from "react"
import { DateTimeValidationError } from "@mui/x-date-pickers"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField"
import dayjs, { Dayjs } from "dayjs"
import utc from "dayjs/plugin/utc"
import tz from "dayjs/plugin/timezone"
import advancedFormat from "dayjs/plugin/advancedFormat"
import { MdFastRewind, MdFastForward, MdOutlineClose, MdOutlineAccessTime, MdOpenInFull } from "react-icons/md"
import styled from "styled-components"

import {
	MobergTheme,
	MobergButton,
	MobergButtonVariant,
	MobergButtonShape,
	MobergFontFamily,
} from "../../../../../../Moberg"
import { MobergHeader } from "../../../../../../Components/MobergHeader/MobergHeader"
import { MobergIconSize } from "../../../../../../Components/MobergIcon/MobergIcon"
import { FieldLabel, FieldError } from "../../../../../../Constants/StyledComponents"
import { ModalityJumpTarget } from "../../Utils/modalityJumpTargets"
import { formatDate } from "../../utils"
import { MobergFontSize } from "../../../../../../Components/MobergFont/MobergFont"
import { toSecondPrecision, clampStartTime, describeFieldError } from "../../Utils/timeRangeUtils"

dayjs.extend(utc)
dayjs.extend(tz)
dayjs.extend(advancedFormat)

interface SelectTimeRangeProps {
	fileStart: Date
	fileEnd: Date
	timeZone: string
	viewDurationLabel: string
	viewDurationMs: number
	initialStart: Date
	onSave: (startMs: number, endMs: number) => void
	onClose: () => void
	modalityTargets?: ModalityJumpTarget[]
	liveModeEnabled?: boolean
}

export function SelectTimeRangeModal({
	fileStart,
	fileEnd,
	timeZone,
	viewDurationLabel,
	viewDurationMs,
	initialStart,
	onSave,
	onClose,
	modalityTargets,
	liveModeEnabled,
}: SelectTimeRangeProps) {
	const minStart = useMemo(
		() => toSecondPrecision(dayjs(fileStart).tz(timeZone)),
		[fileStart, timeZone],
	)
	const maxStart = useMemo(
		() => toSecondPrecision(dayjs(fileEnd).subtract(viewDurationMs, "millisecond").tz(timeZone)),
		[fileEnd, viewDurationMs, timeZone],
	)
	const [start, setStart] = useState<Dayjs>(() =>
		clampStartTime(toSecondPrecision(dayjs(initialStart).tz(timeZone)), minStart, maxStart),
	)
	const dateFormatOpts = { timeZone, timeZoneName: true, seconds: true }

	const [fieldError, setFieldError] = useState<DateTimeValidationError>(null)
	const errorMessage = fieldError !== null ? describeFieldError(fieldError) : ""

	const shouldRenderModalitySection = !!modalityTargets?.length && !liveModeEnabled

	const saveAndClose = (startTime: Dayjs) => {
		const normalizedStart = clampStartTime(toSecondPrecision(startTime), minStart, maxStart)
		onSave(
			normalizedStart.valueOf(),
			normalizedStart.add(viewDurationMs, "millisecond").valueOf(),
		)
		onClose()
	}

	const handleJumpToStart = (startMs: number) => {
		saveAndClose(toSecondPrecision(dayjs(startMs).tz(timeZone)))
	}

	const handleJumpToEnd = (endMs: number) => {
		saveAndClose(
			toSecondPrecision(dayjs(endMs).subtract(viewDurationMs, "millisecond").tz(timeZone)),
		)
	}

	const onStartTimeChange = (value: Dayjs | null) => {
		setStart(toSecondPrecision(value ?? minStart))
	}

	return (
		<ModalContainer>
			<ModalHeader>
				<ModalTitle>Jump to time</ModalTitle>
				<MobergButton
					theme={MobergTheme.BLACK}
					variant={MobergButtonVariant.CLEAR}
					shape={MobergButtonShape.SQUARE}
					onClick={onClose}
				>
					<MdOutlineClose size={MobergIconSize.LARGE} />
				</MobergButton>
			</ModalHeader>

			<div style={{ padding: "16px 24px" }}>
				<InfoPanel>
					<InfoRow>
						<InfoLabel><MdOutlineAccessTime size={14} />File start</InfoLabel>
						<InfoValue>{formatDate(fileStart, dateFormatOpts)}</InfoValue>
					</InfoRow>
					<InfoRow>
						<InfoLabel>
							<MdOutlineAccessTime size={14} style={{ transform: "rotate(180deg)" }} />
							File end
						</InfoLabel>
						<InfoValue>{formatDate(fileEnd, dateFormatOpts)}</InfoValue>
					</InfoRow>
					<InfoRow>
						<InfoLabel>
							<MdOpenInFull size={14} style={{ transform: "rotate(45deg)" }} />
							Window size
						</InfoLabel>
						<InfoValue>{viewDurationLabel}</InfoValue>
					</InfoRow>
				</InfoPanel>

				<LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
					<>
						<FieldLabel>Start time</FieldLabel>
						<DateTimeField
							data-testid="start-time-field"
							value={start}
							onChange={onStartTimeChange}
							onError={setFieldError}
							minDateTime={minStart}
							maxDateTime={maxStart}
							format="MMM D, YYYY, h:mm:ss A"
							fullWidth
							size="small"
							sx={{
								"& .MuiOutlinedInput-root": {
									color: "var(--color-text-primary)",
									backgroundColor: "var(--color-input-bg)",
									"& fieldset": { borderColor: "var(--color-border-default)" },
									"&:hover fieldset": { borderColor: "var(--color-accent-interactive)" },
									"&.Mui-focused fieldset": { borderColor: "var(--color-accent-interactive)" },
								},
								"& .MuiInputBase-input": {
									color: "var(--color-text-primary)",
								},
							}}
						/>
						{errorMessage && <FieldError data-testid="field-error">{errorMessage}</FieldError>}
					</>
				</LocalizationProvider>

				{shouldRenderModalitySection && (
					<div style={{ marginTop: 24 }}>
						<MobergHeader style={{ marginBottom: 4 }}>Modalities</MobergHeader>
						<HelpText>
							Use the rewind/forward buttons to jump to the start or end time of a
							modality.
						</HelpText>
						<ModalityTable>
							<ScrollableContainer>
								{modalityTargets?.map((target, idx) => (
									<ModalityRow
										key={target.label}
										$showBorder={idx < modalityTargets.length - 1}
									>
										<div style={{ flex: 1, fontSize: MobergFontSize.REGULAR }}>
											{target.label}
										</div>
										<div
											style={{
												display: "flex",
												gap: 6,
												width: 140,
												justifyContent: "right",
											}}
										>
											<MobergButton
												data-testid={`jump-to-start-${target.label}`}
												theme={MobergTheme.MEDIUM_GRAY}
												variant={MobergButtonVariant.OUTLINE}
												shape={MobergButtonShape.RECTANGLE}
												onClick={() => handleJumpToStart(target.start)}
												tooltip="Jump to start"
												style={{ padding: "4px 12px" }}
											>
												<MdFastRewind size={16} />
											</MobergButton>
											<MobergButton
												data-testid={`jump-to-end-${target.label}`}
												theme={MobergTheme.MEDIUM_GRAY}
												variant={MobergButtonVariant.OUTLINE}
												shape={MobergButtonShape.RECTANGLE}
												onClick={() => handleJumpToEnd(target.end)}
												tooltip="Jump to end"
												style={{ padding: "4px 12px" }}
											>
												<MdFastForward size={16} />
											</MobergButton>
										</div>
									</ModalityRow>
								))}
							</ScrollableContainer>
						</ModalityTable>
					</div>
				)}
			</div>

			<ModalFooterBar>
				<MobergButton
					data-testid="go-to-time-button"
					theme={MobergTheme.BLUE}
					variant={MobergButtonVariant.FILLED}
					disabled={fieldError !== null}
					onClick={() => saveAndClose(start)}
				>
					Go to time
				</MobergButton>
			</ModalFooterBar>
		</ModalContainer>
	)
}

const ModalContainer = styled.div`
	width: 460px;
	background: var(--color-surface-panel);
	color: var(--color-text-primary);
	border-radius: 4px;
`

export const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px 8px 24px;
	border-bottom: 1px solid var(--color-border-default);
`

export const ModalTitle = styled.h2`
	font-family: ${MobergFontFamily.HEADER};
	font-size: ${MobergFontSize.LARGE};
	font-weight: 600;
	margin: 0;
	color: var(--color-text-primary);
`

const ModalFooterBar = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 12px 24px;
	border-top: 1px solid var(--color-border-default);
`

const HelpText = styled.div`
	font-size: ${MobergFontSize.SMALL};
	color: var(--text-secondary-color);
	margin-bottom: 8px;
`

const InfoPanel = styled.div`
	padding: 0;
	margin-bottom: 16px;
	display: flex;
	flex-direction: column;
	gap: 4px;
`

const InfoRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: ${MobergFontSize.SMALL};
`

const InfoLabel = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
	color: var(--text-secondary-color);
	font-size: ${MobergFontSize.SMALL};
	font-weight: 500;
`

const InfoValue = styled.span`
	color: var(--color-text-primary);
	font-weight: 600;
	font-variant-numeric: tabular-nums;
`

const ModalityTable = styled.div`
	border: 1px solid var(--color-border-default);
	border-radius: 4px;
	overflow: hidden;
`

const ModalityRow = styled.div<{ $showBorder: boolean }>`
	display: flex;
	gap: 12px;
	align-items: center;
	padding: 8px 12px;
	color: var(--color-text-primary);
	border-bottom: ${({ $showBorder }) => ($showBorder ? "1px solid var(--color-border-default)" : "none")};
`

const ScrollableContainer = styled.div`
	max-height: 250px;
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: var(--color-scrollbar-thumb) var(--color-scrollbar-track);

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: var(--color-scrollbar-track);
	}

	&::-webkit-scrollbar-thumb {
		background: var(--color-scrollbar-thumb);
		border-radius: 4px;
	}
`
